import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";

import { PriceId, type TPriceId } from "@musicfy/contants/Subscriptions";

/** Promo code for the Founding 1,000 / Drop #1 offer (Professional annual only). */
export const FOUNDING_PROMO_CODE = "WELCOME50";

/** Paddle discount id for WELCOME50. */
export const FOUNDING_DISCOUNT_ID = "dsc_01kt3d97pfrkwmwx66wzhwqh21";

/**
 * Stripe coupon id matching Paddle WELCOME50: 50% off first period, Pro
 * yearly only, 1,000 redemptions. Same id in Test and Live.
 */
export const STRIPE_FOUNDING_COUPON_ID = "musicfy_welcome_50";

/** Only price WELCOME50 applies to. */
export const FOUNDING_PRICE_ID: TPriceId = PriceId.pro_year;

export const FOUNDING_SEAT_CAP = 1000;

export const FOUNDING_FIRST_YEAR_DISPLAY = "$120.00";
export const FOUNDING_RENEWAL_DISPLAY = "$239.99";

/** Attribution for Drop #1 conversions in Paddle customData. */
export const FOUNDING_CUSTOM_DATA = {
  source: "drop1",
  cohort: "founding1000",
} as const;

/**
 * Email / deep-link founding path stays available without an eng-updated
 * hard date. Paddle still rejects expired or exhausted WELCOME50 codes.
 */
export const isFoundingOfferLive = (): boolean => true;

export const isFoundingPromoCode = (code: unknown): boolean =>
  typeof code === "string" && code.trim().toUpperCase() === FOUNDING_PROMO_CODE;

export const isFoundingDiscountApplied = (
  discount?: {
    code?: string | null;
    id?: string | null;
  } | null
): boolean => {
  if (!discount) return false;
  if (discount.id === FOUNDING_DISCOUNT_ID) return true;
  if (discount.id === STRIPE_FOUNDING_COUPON_ID) return true;
  return isFoundingPromoCode(discount.code);
};

export type FoundingEligibilityInput = {
  plan: string | null | undefined;
  interval: string | null | undefined;
  /** Viewer's current subscription plan (free / starter / pro / …). */
  subscriptionPlan: string | null | undefined;
  /** Viewer's current billing interval — used to detect already-on Pro annual. */
  subscriptionInterval?: string | null | undefined;
  /**
   * While the subscription query is in flight, hide the block so it does not
   * flash for users who will be ineligible once the plan resolves.
   */
  isSubscriptionLoading?: boolean;
  /** false hides; null/undefined = unknown → fail-open (show). */
  seatsAvailable?: boolean | null;
};

/**
 * True when the viewer cannot claim a Founding seat: already on Pro annual, or
 * on a higher tier (Studio / Label). Starter and Pro monthly *should* see it so
 * they can upgrade into the offer.
 */
export const isAlreadyOnFoundingTargetPlan = (
  subscriptionPlan: string | null | undefined,
  subscriptionInterval?: string | null | undefined
): boolean => {
  if (
    subscriptionPlan === SubscriptionPlans.studio ||
    subscriptionPlan === SubscriptionPlans.label
  ) {
    return true;
  }

  return (
    subscriptionPlan === SubscriptionPlans.pro &&
    subscriptionInterval === SubscriptionIntervals.year
  );
};

/**
 * Founding UI gate. Professional + annual card only + live window.
 * Show for free, logged-out, Starter, and Pro monthly. Hide for Pro annual /
 * Studio / Label, while subscription is loading, and when seats are exhausted.
 */
export const shouldShowFoundingBlock = ({
  plan,
  interval,
  subscriptionPlan,
  subscriptionInterval,
  isSubscriptionLoading,
  seatsAvailable,
}: FoundingEligibilityInput): boolean => {
  if (!isFoundingOfferLive()) return false;
  if (plan !== SubscriptionPlans.pro) return false;
  if (interval !== SubscriptionIntervals.year) return false;
  if (isSubscriptionLoading) return false;
  if (isAlreadyOnFoundingTargetPlan(subscriptionPlan, subscriptionInterval)) {
    return false;
  }
  if (seatsAvailable === false) return false;
  return true;
};

export const foundingDiscountArg = (): { code: string } => ({
  code: FOUNDING_PROMO_CODE,
});
